<template>
  <div>
    <b-alert
      v-if="errors.length"
      class="tw-mb-4"
      variant="danger"
      dismissible
      show
      @dismissed="errors = []"
    >
      <ul class="tw-list-none tw-pl-4">
        <li v-for="(error, index) in errors" :key="index">
          <b-icon icon="exclamation-circle" class="tw-mr-2 tw-text-xl" />
          {{ error.detail || error.message || error }}
        </li>
      </ul>
    </b-alert>

    <b-alert
      v-if="success"
      class="tw-mb-4"
      variant="success"
      dismissible
      show
      @dismissed="success = false"
    >
      <b-icon icon="check-circle" class="tw-mr-2 tw-text-xl" />
      {{ $t("template.phone_login_success", "Phone login set successfully") }}
    </b-alert>

    <div v-if="isPhoneStep" class="form-group form-unit">
      <VueTelInput
        name="phone"
        v-model="phone"
        v-validate="{
          required: true,
          twilio_lookup: twilioLookup,
        }"
        :inputOptions="{
          styleClasses: 'form-control form-unit__input',
        }"
        mode="international"
        data-vv-validate-on="blur"
        valid-characters-only
        tabindex="4"
        @validate="validate"
        :defaultCountry="defaultCountry"
        :autoDefaultCountry="autoDefaultCountry"
        :disabled="disabled"
      />
      <span
        v-if="vErrorsHas('phone', 'required') || vErrorsHas('phone', 'decimal')"
        class="v-help is-danger"
      >
        {{ $t("template.field_is_required") }}
      </span>
      <span v-if="twilioLookupErrorMsg" class="v-help is-danger">
        {{ twilioLookupErrorMsg }}
      </span>
    </div>

    <div v-else>
      <phone-code-input
        :loading="loading"
        :phoneCodeDigits="phoneCodeDigits"
        @update:phoneCodeDigits="phoneCodeDigits = $event"
        @submitPhoneCode="setPhoneLogin"
      />
      <span v-if="vErrorsHas('phone', 'twilio_lookup')" class="v-help is-danger">
        {{ twilioLookupErrorMsg }}
      </span>
    </div>

    <!-- actions -->
    <div v-if="!disabled" class="tw-flex tw-justify-center tw-mt-4">
      <div
        v-if="!isPhoneCodeStep"
        type="button"
        class="bttn bttn--lg bttn--orange"
        :disabled="loading"
        @click="setPhoneLogin"
      >
        {{ $t("template.save_phone_login", "Save") }}
      </div>

      <button
        v-if="isPhoneCodeStep"
        class="tw-mt-[36px] tw-mx-[5px] bttn bttn--lg bttn--blue tw-px-6"
        :disabled="loading"
        @click.prevent="activeStep = 'phone'"
      >
        {{ $t("template.log_in_back", "Back") }}
      </button>
      <button
        v-if="isPhoneCodeStep"
        type="button"
        class="tw-mt-[36px] tw-mx-[5px] bttn bttn--lg bttn--orange tw-py-2"
        :disabled="loading"
        @click="setPhoneLogin"
      >
        {{ $t("template.sign_in_send_code", "Send Code") }}
      </button>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";

import { VueTelInput } from "vue-tel-input";
import PhoneCodeInput from "./phoneCodeInput.vue";
import axios from "axios";
import urls from "@/constants/urls";

export default defineComponent({
  name: "AccountPhoneLogin",
  components: {
    VueTelInput,
    PhoneCodeInput,
  },
  props: {
    defaultCountry: {
      type: String,
      default: "N/A",
    },
  },

  data() {
    return {
      phone: "",
      code_token: "",

      phoneCodeDigits: ["", "", "", "", "", ""],
      loading: false,
      activeStep: "phone",
      phoneObject: null,
      errors: [],

      disabled: false,
      success: false,
    };
  },

  mounted() {
    this.getLoginPhone();
  },

  computed: {
    isPhoneStep() {
      return this.activeStep === "phone";
    },
    isPhoneCodeStep() {
      return this.activeStep === "enterPhoneCode";
    },
    twilioLookupErrorMsg() {
      const exist = this.vErrors.items.find(
        (el) => el.field === "phone" && el.rule === "twilio_lookup"
      );

      if (exist) {
        return this.$t("template.field_error_twilio_lookup", "Invalid or incorrect phone number");
      }

      return "";
    },
    twilioLookup() {
      return {
        valid: this.phoneObject?.valid || false,
      };
    },
    autoDefaultCountry() {
      return this.defaultCountry === "N/A" || this.defaultCountry.length === 0;
    },
  },

  methods: {
    validate(phoneObject) {
      this.phoneObject = phoneObject;

      if (!this.phoneObject?.valid) {
        return;
      }
    },
    vErrorsHas(field, rule) {
      return this.vErrors.items.find((el) => el.field === field && el.rule === rule);
    },

    getLoginPhone() {
      this.loading = true;
      return axios
        .get(urls.signin)
        .then((response) => {
          this.phone = response.data.data.attributes.phone || "";
          this.disabled = !!this.phone;
        })
        .finally(() => {
          this.loading = false;
        });
    },

    setPhoneLogin() {
      if (this.disabled) {
        return;
      }

      if (!this.phoneObject?.valid && !this.isPhoneCodeStep && this.vErrors.items.length === 0) {
        this.$set(this.vErrors, "items", [
          ...this.vErrors.items,
          { field: "phone", rule: "twilio_lookup" },
        ]);
        return;
      }

      if (this.vErrorsHas("phone", "twilio_lookup")) {
        return;
      }

      this.loading = true;

      const payload = {
        phone: this.phone,
        code: this.phoneCodeDigits.join(""),
        code_token: this.code_token,
      };

      axios
        .post(urls.accountPhoneLogin, payload)
        .then((response) => {
          if (response.data?.data?.code_token) {
            this.code_token = response.data.data.code_token || "";
            this.activeStep = "enterPhoneCode";
            return;
          }

          return this.getLoginPhone().then(() => {
            this.activeStep = "phone";
            this.success = true;
            this.phoneCodeDigits = ["", "", "", "", "", ""];
            this.code_token = "";
            this.errors = [];
          });
        })
        .catch((error) => {
          if (error.response?.status === 500) {
            this.errors = [
              {
                message: this.$t("template.error_server", "Server error"),
              },
            ];
            return;
          }
          this.errors = error.response?.data?.errors || [];
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
});
</script>
